import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { AgenteParametroDTO, CreateAgenteParametroRequest, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { DUE_DATES_BACK_PATH, errorMessage } from '../common/due-date-common'

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

/** Valores por `parametroId`, como texto para permitir capturar el signo `-`. */
export type DueDateFormState = Record<number, string>

export interface DueDateRamo {
    id: string
    label: string
    ramo: string
    primerReciboId: number
    subsecuenteId: number
}

/** `parametroId` fijos del backend para los días de vencimiento por ramo. */
export const DUE_DATE_RAMOS: DueDateRamo[] = [
    { id: 'vida', label: 'Vida', ramo: 'vida', primerReciboId: 5, subsecuenteId: 6 },
    { id: 'autos', label: 'Autos', ramo: 'autos', primerReciboId: 1, subsecuenteId: 2 },
    { id: 'salud', label: 'Salud', ramo: 'salud', primerReciboId: 3, subsecuenteId: 4 },
    { id: 'danos', label: 'Daños', ramo: 'daños', primerReciboId: 7, subsecuenteId: 8 },
]

const PARAM_IDS = DUE_DATE_RAMOS.flatMap((ramo) => [ramo.primerReciboId, ramo.subsecuenteId])

const LOAD_ERROR = 'No se pudieron cargar las fechas de vencimiento.'
const SAVE_ERROR = 'Error al guardar las fechas de vencimiento'
const INVALID_ERROR = 'Captura un número de días en todos los campos.'

const emptyState = (): DueDateFormState => Object.fromEntries(PARAM_IDS.map((id) => [id, '0']))

const toFormState = (items: AgenteParametroDTO[]): DueDateFormState => {
    const state = emptyState()
    PARAM_IDS.forEach((id) => {
        const valor = items.find((item) => item.parametroId === id)?.valor
        if (valor != null && valor !== '') state[id] = String(parseInt(valor, 10) || 0)
    })
    return state
}

const toRequest = (state: DueDateFormState): CreateAgenteParametroRequest[] =>
    [...PARAM_IDS].sort((left, right) => left - right).map((id) => ({
        parametroId: id,
        tipoValor: 'entero',
        valor: String(parseInt(state[id], 10) || 0),
    }))

/** Entero con signo opcional, máximo 4 caracteres como en el original. */
const sanitizeDays = (value: string) => {
    const negative = value.trim().startsWith('-')
    const digits = value.replace(/\D/g, '').slice(0, negative ? 3 : 4)
    return `${negative ? '-' : ''}${digits}`
}

const isValidDays = (value: string) => /^-?\d+$/.test(value)

export const dueDateHint = (value: string) => {
    if (!isValidDays(value)) return 'Captura un número de días.'
    const days = parseInt(value, 10)
    if (days === 0) return 'Vence el mismo día de la vigencia.'
    const abs = Math.abs(days)
    return `Vence ${abs} ${abs === 1 ? 'día' : 'días'} ${days > 0 ? 'después' : 'antes'} de la vigencia.`
}

export const useDueDateFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || DUE_DATES_BACK_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [state, setState] = useState<DueDateFormState>(emptyState)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<AgenteParametroDTO[]> | null = await (await APIInvoke.invokeGET('/parametros')).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                setState(toFormState(response.body || []))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    const change = (parametroId: number, value: string) =>
        setState((current) => ({ ...current, [parametroId]: sanitizeDays(value) }))

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (PARAM_IDS.some((id) => !isValidDays(state[id]))) {
            setMessage({ type: 'error', message: INVALID_ERROR })
            return
        }
        try {
            setSaving(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokePOST('/parametros', toRequest(state))).json()
            setMessage(response.ok
                ? { type: 'success', message: response.message || 'Parámetros actualizados correctamente' }
                : { type: 'error', message: response.message || SAVE_ERROR })
        } catch (error) {
            console.error('error al guardar los vencimientos', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    return {
        form: state,
        loading,
        saving,
        busy: loading || saving,
        busyLabel: loading ? 'Cargando configuración…' : 'Guardando configuración…',
        message,
        backTo: backUrl,
        change,
        save,
        cancel: () => history.push(backUrl),
    }
}
